import { useMemo } from 'react';
import {
  EnhancedGroupedSection,
  FormSchemaConfig,
} from '../../types/form/schema.types';
import { FormState } from '../../types';

// ============= Component =============
export const useSecondPartnerSchema = (
  formState: FormState
): FormSchemaConfig => {
  // const secondPartnerData = formState.data?.second_partner;

  return useMemo<FormSchemaConfig>(() => {
    const isEditAllowed = Array.isArray(formState.data?.userAssignedSections)
      ? (formState.data?.userAssignedSections as string[]).includes(
          'second_partner'
        )
      : false;
    // Safely access the responsible lawyer data
    // const secondPartner =
    //   secondPartnerData?.[0]?.label || secondPartnerData || '';
    // Create the main section that matches EnhancedGroupedSection type
    const secondPartnerSection: EnhancedGroupedSection = {
      // profile: {
      //   name: secondPartner,
      //   variant: 'row',
      //   theme: 'blue',
      //   job: 'Second Partner',
      // },
      layout: 'single',
      isGrouped: true,
      isGroupedAccordion: false,
      sections: [
        {
          title: {
            text: 'Payment Authorisation - 2nd Partner (Only required on payment above £25,000)',
          },
          layout: 'single',
          fields: [
            {
              type: 'radio',
              name: 'second_approver',
              label: 'Do you wish to authorise this payment?',
              options: [
                {
                  value: 'yes',
                  label: 'Yes',
                  disabled: false,
                },
                {
                  value: 'no',
                  label: 'No',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value) =>
                !value
                  ? 'Please select whether you authorize this payment'
                  : undefined,
              disabled:
                formState.completedSections?.includes('second_partner') ||
                !isEditAllowed,
            },
            {
              type: 'textArea',
              name: 'second_reject_details',
              label: 'Rejection Details',
              required: true,
              validate: (value) =>
                !value ? 'Please provide details for the rejection' : undefined,
              disabled:
                formState.completedSections?.includes('second_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'second_approver',
                      operator: 'equals',
                      value: 'no',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'second_verify_details',
              label:
                'I have reviewed the relevant elements of the underlying transaction or other documentation and confirm they are consistent with the payment request details (e.g. number of payments, values to be paid, beneficiary names, reason for payment):',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.second_approver === 'yes' && !value
                  ? 'Please acknowledge if you have reviewed the documentation'
                  : undefined,
              disabled:
                formState.completedSections?.includes('second_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'second_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'second_verify_bank',
              label:
                'I am satisfied that the bank details have been verified and appropriately confirmed by the Responsible Lawyer',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.second_approver === 'yes' && !value
                  ? 'Please acknowledge if you have reviewed the bank details'
                  : undefined,
              disabled:
                formState.completedSections?.includes('second_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'second_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'second_verify_currency',
              label: 'I have confirmed the transaction currency:',
              options: [
                {
                  value: 'verified',
                  label: 'Acknowledge',
                  disabled: false,
                },
              ],
              required: true,
              validate: (value, formData) =>
                formData?.second_approver === 'yes' && !value
                  ? 'Please acknowledge if you have confirmed the transaction currency'
                  : undefined,
              disabled:
                formState.completedSections?.includes('second_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'second_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'checkbox',
              name: 'second_verify_reason',
              required: true,
              validate: (value, formData) =>
                formData?.second_approver === 'yes' && !value
                  ? 'Please acknowledge if you have reviewed the reason for the payment'
                  : undefined,
              label:
                'As an approving partner I have sufficient understanding of the reason for the payment:',
              options: [
                { value: 'verified', label: 'Acknowledge', disabled: false },
              ],
              disabled:
                formState.completedSections?.includes('second_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'second_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
            {
              type: 'textArea',
              name: 'second_comments',
              label: 'Additional Comments',
              disabled:
                formState.completedSections?.includes('second_partner') ||
                !isEditAllowed,
              dependencies: [
                {
                  type: 'show',
                  logicalOperator: 'AND',
                  conditions: [
                    {
                      field: 'second_approver',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      extras: { grouped: true, evenSpacing: true },
    };

    return {
      sections: [secondPartnerSection],
      dependencies: {
        showWhen: [
          {
            field: 'first_partner', // Second partner depends on first partner completion
          },
        ],
      },
    };
  }, [formState]);
};
